import { QueryClient } from '@tanstack/react-query';
import { ApiError } from '@pc/api/errors';

/**
 * Rate limits are 120/min per actor and 60/min per IP pre-auth, and the handoff
 * is explicit that jobs must not be polled aggressively. The retry policy below
 * is therefore conservative, and — more importantly — refuses to retry answers
 * that will not change.
 */

/** Ceiling on automatic retries for the failures that are worth retrying. */
const MAX_RETRIES = 3;

/**
 * Ceiling on the wait between two attempts, whichever branch below produced it.
 *
 * It applies to the `Retry-After` branch as much as to the exponential one,
 * because the cost of a long wait is paid by the operator either way. A query
 * that is retrying is `isPending`, and a pending query renders a spinner and no
 * error: a 429 carrying `Retry-After: 60` would hold three whole minutes of
 * silence before "Too many requests" was ever put on screen, and there is
 * nothing on that screen to distinguish it from a request that has hung. The
 * header is still obeyed below this, which is every value the API actually
 * sends; above it, the honest thing is to stop waiting and say what happened —
 * `ApiErrorNotice` renders the server's real wait in words and gives the
 * operator the retry.
 */
const MAX_RETRY_DELAY_MS = 15_000;

export function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= MAX_RETRIES) return false;

  if (error instanceof ApiError) {
    // A 403 is a real answer about this actor. A 404 is a real answer about
    // this resource. Retrying either just burns quota to be told the same
    // thing. A 503 is the opposite case: nobody could check, so ask again.
    return error.isRetryable;
  }

  // Unknown failure — likely a bug in our own code. Do not paper over it.
  return false;
}

export function retryDelay(failureCount: number, error: unknown): number {
  // When the server told us how long to wait, obey it rather than guessing —
  // up to the point where obeying it means showing the operator nothing for
  // longer than they will believe the screen is working. See the ceiling above.
  if (error instanceof ApiError && error.retryAfterSeconds !== undefined) {
    return Math.min(error.retryAfterSeconds * 1000, MAX_RETRY_DELAY_MS);
  }
  // Otherwise exponential backoff, capped so a long outage does not leave the
  // UI silently waiting minutes between attempts.
  return Math.min(1000 * 2 ** failureCount, MAX_RETRY_DELAY_MS);
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        retryDelay,
        // Reads here are operational data an operator is actively looking at;
        // a short stale window keeps it current without hammering the API.
        staleTime: 15_000,
        refetchOnWindowFocus: true,
      },
      mutations: {
        // Mutations are power operations and lifecycle changes. Never retry
        // one automatically — a duplicated reboot is a real-world event.
        retry: false,
      },
    },
  });
}
